import React, { useEffect, useState, useMemo } from "react";
import config from '../config.js'; // Adjust path as needed based on your folder structure
import { apiErrorMessage } from '../utils/apiError';

/**
 * ShopHealthPage
 * * Manages Maintenance Definitions (Shop Upkeep & Maintenance).
 * * UPDATED: Includes Delete functionality, Manual Task Scheduling, and File Attachments.
 */

export default function ShopHealthPage() {
  const API_BASE = config.API_BASE_URL;

  // --- LEFT PANEL STATE ---
  const [rules, setRules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [stationFilter, setStationFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [selectedRule, setSelectedRule] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- RIGHT PANEL STATE ---
  const [defId, setDefId] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taskType, setTaskType] = useState("Maintenance"); // 'Maintenance' or 'Shop Upkeep'
  const [workstation, setWorkstation] = useState("Laser");
  const [isActive, setIsActive] = useState(true);
  const [blockProduction, setBlockProduction] = useState(false);
  
  // Trigger Logic
  const [triggerType, setTriggerType] = useState("Calendar"); // 'Calendar', 'Usage', 'Manual'
  const [recurrenceDays, setRecurrenceDays] = useState(""); // For Calendar
  const [usageThreshold, setUsageThreshold] = useState(""); // For Usage (Hours)
  const [nextRunDate, setNextRunDate] = useState(""); // For Manual Scheduling
  
  // Status / Read-only data
  const [currentUsage, setCurrentUsage] = useState(0);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [initialEstMinutes, setInitialEstMinutes] = useState(60);

  // Checklist Steps Logic
  const [checklistSteps, setChecklistSteps] = useState([]);
  const [newStepText, setNewStepText] = useState("");

  // Day of week
  const [recurrenceDayOfWeek, setRecurrenceDayOfWeek] = useState("");

  // Scheduled time (Calendar triggers)
  const [scheduledTime, setScheduledTime] = useState("");

  // Skip if P1 task on same workstation
  const [skipIfPriority1, setSkipIfPriority1] = useState(false);

  // Documents / Attachments
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Error/Success
  const [error, setError] = useState(null);

  // --- REFERENCE DATA ---
  const stations = ["Laser", "Tap", "Brake", "Fabrication", "Blast", "Finish", "Shipping", "General Shop"];

  useEffect(() => {
    loadRules();
  }, []);

  // --- API CALLS ---

  async function loadRules() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/maintenance/definitions`);
      if (!res.ok) throw new Error(`Failed to load rules: ${res.status}`);
      const data = await res.json();
      setRules(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    // Validation
    if (!title.trim()) { alert("Title is required."); return; }
    if (triggerType === "Calendar" && (!recurrenceDays || recurrenceDays <= 0)) {
        alert("Please enter a valid number of days for recurrence."); return;
    }
    if (triggerType === "Usage" && (!usageThreshold || usageThreshold <= 0)) {
        alert("Please enter a valid hour threshold for usage."); return;
    }

    const payload = {
        title,
        description,
        task_type: taskType,
        workstation,
        trigger_type: triggerType,
        recurrence_days: triggerType === "Calendar" ? parseInt(recurrenceDays) : null,
        recurrence_day_of_week: (triggerType === "Calendar" && recurrenceDayOfWeek) ? recurrenceDayOfWeek : null,
        usage_threshold_hours: triggerType === "Usage" ? parseFloat(usageThreshold) : 0,
        initial_est_minutes: parseFloat(initialEstMinutes),
        is_active: isActive,
        checklist_steps: checklistSteps,
        block_production: blockProduction,
        next_run_date: triggerType === "Manual" ? (nextRunDate || null) : null,
        scheduled_time: (triggerType === "Calendar" || triggerType === "Manual") ? (scheduledTime || null) : null,
        skip_if_priority1: skipIfPriority1
    };

    let url = `${API_BASE}/maintenance/definitions`;
    let method = "POST";

    if (defId) {
        url = `${API_BASE}/maintenance/definitions/${defId}`;
        method = "PATCH";
    }

    try {
        const res = await fetch(url, {
            method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
        
        const saved = await res.json();
        alert(defId ? "Rule Updated" : "Rule Created");
        
        await loadRules();
        
        // If created new, select it
        if (!defId) {
            handleSelectRule(saved);
        } else {
            // If updated, refresh the selected view with new server data
            fillForm(saved);
        }

    } catch (err) {
        setError(err.message);
    }
  }

  async function handleDelete() {
    if (!defId) return;
    if (!window.confirm("Are you sure you want to delete this maintenance rule? This action cannot be undone.")) return;

    try {
        const res = await fetch(`${API_BASE}/maintenance/definitions/${defId}`, {
            method: "DELETE"
        });
        
        if (!res.ok) {
            throw new Error(await apiErrorMessage(res, "Failed to delete rule"));
        }

        const data = await res.json();
        alert(data.message || "Rule deleted.");
        
        // Refresh list and reset form
        await loadRules();
        handleCreateNew();

    } catch (err) {
        alert("Error deleting rule: " + err.message);
    }
  }

  async function handleRunChecks() {
      if(!window.confirm("This will evaluate all active rules and generate tasks if criteria are met. Continue?")) return;
      try {
          const res = await fetch(`${API_BASE}/maintenance/run_checks`, { method: 'POST' });
          const data = await res.json();
          alert(data.message);
      } catch (err) {
          alert("Error running checks: " + err.message);
      }
  }

  async function handleSpawnNow() {
      if (!defId) return;
      if (!window.confirm("Force create this task now?")) return;
      
      try {
          const res = await fetch(`${API_BASE}/maintenance/definitions/${defId}/spawn`, { method: "POST" });
          if (!res.ok) throw new Error(await apiErrorMessage(res));
          const data = await res.json();
          alert(data.message);
          loadRules(); // Refresh to see last_generated update
      } catch (err) {
          alert("Error spawning task: " + err.message);
      }
  }

  // --- DOCUMENT HANDLERS ---

  async function handleUpload(e) {
      if (!defId) { alert("Please save the rule first before adding documents."); return; }
      const file = e.target.files[0];
      if (!file) return;

      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("display_name", file.name); // Simple default

      try {
          const res = await fetch(`${API_BASE}/maintenance/definitions/${defId}/documents`, {
              method: "POST",
              body: formData
          });
          if (!res.ok) throw new Error(await apiErrorMessage(res));
          
          // Refresh documents list
          const docsRes = await fetch(`${API_BASE}/maintenance/definitions/${defId}/documents`);
          if (docsRes.ok) setDocuments(await docsRes.json());
          
      } catch (err) {
          alert("Upload failed: " + err.message);
      } finally {
          setIsUploading(false);
          e.target.value = null; // Reset input
      }
  }

  async function handleDeleteDoc(docId) {
      if(!window.confirm("Delete this document?")) return;
      try {
          await fetch(`${API_BASE}/maintenance/documents/${docId}`, { method: "DELETE" });
          setDocuments(documents.filter(d => d.document_id !== docId));
      } catch (err) {
          alert(err.message);
      }
  }

  // --- FORM LOGIC ---

  function handleCreateNew() {
    setSelectedRule(null);
    setDefId(null);
    setTitle("");
    setDescription("");
    setTaskType("Maintenance");
    setWorkstation("Laser");
    setTriggerType("Calendar");
    setRecurrenceDays("7"); // Default weekly
    setRecurrenceDayOfWeek("");
    setUsageThreshold("");
    setNextRunDate("");
    setInitialEstMinutes(30);
    setIsActive(true);
    setBlockProduction(false);
    setCurrentUsage(0);
    setLastGenerated(null);
    setChecklistSteps([]);
    setNewStepText("");
    setScheduledTime("");
    setSkipIfPriority1(false);
    setDocuments([]); // Clear docs
    setError(null);
  }

  function handleSelectRule(rule) {
    setSelectedRule(rule);
    fillForm(rule);
  }

  async function fillForm(rule) {
    setDefId(rule.definition_id);
    setTitle(rule.title || "");
    setDescription(rule.description || "");
    setTaskType(rule.task_type || "Maintenance");
    setWorkstation(rule.workstation || "Laser");
    setTriggerType(rule.trigger_type || "Calendar");
    setIsActive(rule.is_active);
    setBlockProduction(rule.block_production || false);
    setInitialEstMinutes(rule.initial_est_minutes || 60);
    setCurrentUsage(rule.current_usage_hours || 0);
    setLastGenerated(rule.last_generated_at);
    setChecklistSteps(rule.checklist_steps || []);
    setRecurrenceDayOfWeek(rule.recurrence_day_of_week || "");
    setScheduledTime(rule.scheduled_time || "");
    setSkipIfPriority1(rule.skip_if_priority1 || false);
    setNextRunDate(rule.next_run_date || "");

    // Parse Interval for display (Postgres returns "7 days" or "1 day", etc)
    let days = "";
    if (rule.recurrence_interval) {
        // Simple regex to grab the number
        const match = rule.recurrence_interval.match(/(\d+)/);
        if (match) days = match[0];
    }
    setRecurrenceDays(days);
    
    setUsageThreshold(rule.usage_threshold_hours || "");

    // Load Documents
    setDocuments([]);
    try {
        const res = await fetch(`${API_BASE}/maintenance/definitions/${rule.definition_id}/documents`);
        if (res.ok) {
            const docs = await res.json();
            setDocuments(docs);
        }
    } catch (e) {
        console.error("Failed to load documents", e);
    }
  }

  // --- CHECKLIST HANDLERS ---
  const handleAddStep = () => {
      if(!newStepText.trim()) return;
      setChecklistSteps([...checklistSteps, newStepText.trim()]);
      setNewStepText("");
  };

  const handleRemoveStep = (index) => {
      const updated = [...checklistSteps];
      updated.splice(index, 1);
      setChecklistSteps(updated);
  };

  // --- FILTERING ---
  const filteredRules = useMemo(() => {
    return rules.filter(r => {
        if (showActiveOnly && !r.is_active) return false;
        if (stationFilter && r.workstation !== stationFilter) return false;
        if (typeFilter && r.task_type !== typeFilter) return false;
        if (searchTerm) {
            const low = searchTerm.toLowerCase();
            if (!r.title.toLowerCase().includes(low) && !r.description?.toLowerCase().includes(low)) return false;
        }
        return true;
    });
  }, [rules, showActiveOnly, stationFilter, typeFilter, searchTerm]);

  // --- HELPER RENDERS ---
  const getUsagePercent = () => {
      if (!usageThreshold || usageThreshold <= 0) return 0;
      const pct = (currentUsage / usageThreshold) * 100;
      return Math.min(pct, 100);
  };

  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      {/* LEFT PANEL: LISTING */}
      <div className="border-end p-3 bg-light" style={{ width: "320px", flexShrink: 0, overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 fw-bold"><i className="bi bi-heart-pulse-fill text-danger me-2"></i>Shop Health</h5>
          <button className="btn btn-sm btn-primary" onClick={handleCreateNew}><i className="bi bi-plus-lg"></i> New Rule</button>
        </div>

        {/* Filters */}
        <div className="mb-3">
            <input type="text" className="form-control form-control-sm mb-2" placeholder="Search rules..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            <div className="row g-1 mb-2">
                <div className="col-6">
                    <select className="form-select form-select-sm" value={stationFilter} onChange={e => setStationFilter(e.target.value)}>
                        <option value="">All Stations</option>
                        {stations.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div className="col-6">
                    <select className="form-select form-select-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                        <option value="">All Types</option>
                        <option value="Maintenance">Maintenance</option>
                        <option value="Shop Upkeep">Shop Upkeep</option>
                    </select>
                </div>
            </div>
            <div className="form-check">
                <input className="form-check-input" type="checkbox" checked={showActiveOnly} onChange={e => setShowActiveOnly(e.target.checked)} id="showActive" />
                <label className="form-check-label small" htmlFor="showActive">Active Rules Only</label>
            </div>
        </div>

        {/* List */}
        {loading && <div className="text-center text-muted small">Loading...</div>}
        <div className="list-group list-group-flush border rounded">
            {filteredRules.map(r => (
                <button 
                    key={r.definition_id} 
                    className={`list-group-item list-group-item-action py-2 ${selectedRule?.definition_id === r.definition_id ? "active" : ""}`}
                    onClick={() => handleSelectRule(r)}
                >
                    <div className="d-flex w-100 justify-content-between align-items-center">
                        <strong className="mb-1 text-truncate" style={{maxWidth: "180px"}}>
                            {r.block_production && <span title="Blocks Production" className="me-1">🔒</span>}
                            {r.title}
                        </strong>
                        {!r.is_active && <span className="badge bg-secondary" style={{fontSize: "0.6rem"}}>Inactive</span>}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                        <span className={`badge bg-${r.task_type === 'Maintenance' ? 'warning text-dark' : 'info text-dark'}`}>{r.task_type}</span>
                        <small className={selectedRule?.definition_id === r.definition_id ? "text-light" : "text-muted"}>{r.workstation}</small>
                    </div>
                    {/* Tiny Progress Bar for Usage Types */}
                    {r.trigger_type === 'Usage' && r.usage_threshold_hours > 0 && (
                        <div className="progress mt-2" style={{height: "4px"}}>
                            <div className="progress-bar bg-success" role="progressbar" style={{width: `${Math.min((r.current_usage_hours / r.usage_threshold_hours)*100, 100)}%`}}></div>
                        </div>
                    )}
                </button>
            ))}
            {filteredRules.length === 0 && !loading && (
                <div className="p-3 text-center text-muted small">No rules found.</div>
            )}
        </div>
      </div>

      {/* RIGHT PANEL: FORM */}
      <div className="flex-grow-1 p-4 overflow-auto bg-white">
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
            <h4>{defId ? `Edit Rule #${defId}` : "Create New Maintenance Rule"}</h4>
            <div>
                {/* DELETE BUTTON - Only shows if a definition is selected */}
                {defId && (
                    <button className="btn btn-outline-danger me-2" onClick={handleDelete} title="Delete this rule">
                        <i className="bi bi-trash"></i> Delete
                    </button>
                )}
                <button className="btn btn-outline-secondary me-2" onClick={handleRunChecks} title="Force system to check triggers now">
                    <i className="bi bi-play-fill"></i> Run Checks
                </button>
                <button className="btn btn-success" onClick={handleSave}>
                    <i className="bi bi-save me-1"></i> Save Rule
                </button>
            </div>
        </div>

        <div className="row">
            <div className="col-lg-8">
                
                {/* CARD 1: BASIC INFO */}
                <div className="card mb-4 shadow-sm">
                    <div className="card-header bg-light fw-bold">Basic Information</div>
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col-md-8">
                                <label className="form-label">Task Title</label>
                                <input type="text" className="form-control" placeholder="e.g. Clean Laser Lens" value={title} onChange={e => setTitle(e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Status & Security</label>
                                
                                {/* Active Switch */}
                                <div className="form-check form-switch mt-2">
                                    <input className="form-check-input" type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                                    <label className="form-check-label">{isActive ? "Active" : "Paused"}</label>
                                </div>

                                {/* Block Production Switch */}
                                <div className="form-check form-switch mt-2">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        checked={blockProduction} 
                                        onChange={e => setBlockProduction(e.target.checked)} 
                                        style={{ backgroundColor: blockProduction ? '#dc3545' : '', borderColor: blockProduction ? '#dc3545' : '' }} // Red when active
                                    />
                                    <label className="form-check-label text-danger fw-bold">
                                        {blockProduction ? "🔒 Production BLOCKED" : "🔓 Non-Blocking"}
                                    </label>
                                </div>
                                <div className="form-text small" style={{fontSize: '0.75rem', lineHeight: '1.2'}}>
                                    If locked, no production tasks can start until this maintenance is complete.
                                </div>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Type</label>
                                <select className="form-select" value={taskType} onChange={e => setTaskType(e.target.value)}>
                                    <option value="Maintenance">Maintenance (Machine Focused)</option>
                                    <option value="Shop Upkeep">Shop Upkeep (Facility/Cleaning)</option>
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Workstation</label>
                                <select className="form-select" value={workstation} onChange={e => setWorkstation(e.target.value)}>
                                    {stations.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Instructions / Description</label>
                            <textarea className="form-control" rows="3" placeholder="Detailed instructions for the operator..." value={description} onChange={e => setDescription(e.target.value)}></textarea>
                        </div>
                    </div>
                </div>

                {/* CARD 2: CHECKLIST STEPS */}
                <div className="card mb-4 shadow-sm border-secondary">
                    <div className="card-header bg-secondary text-white fw-bold">Required Steps / Checklist</div>
                    <div className="card-body">
                        <div className="input-group mb-3">
                            <input 
                                type="text" 
                                className="form-control" 
                                placeholder="Add a required step..." 
                                value={newStepText} 
                                onChange={e => setNewStepText(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAddStep()}
                            />
                            <button className="btn btn-outline-secondary" type="button" onClick={handleAddStep}>
                                <i className="bi bi-plus-lg"></i> Add
                            </button>
                        </div>
                        
                        {checklistSteps.length === 0 ? (
                            <div className="text-muted small fst-italic">No checklist steps defined.</div>
                        ) : (
                            <ul className="list-group">
                                {checklistSteps.map((step, idx) => (
                                    <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>{idx + 1}. {step}</span>
                                        <button className="btn btn-sm btn-outline-danger border-0" onClick={() => handleRemoveStep(idx)}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* CARD 4: DOCUMENTS & MANUALS (NEW) */}
                {defId && (
                    <div className="card mb-4 shadow-sm">
                        <div className="card-header bg-light fw-bold d-flex justify-content-between align-items-center">
                            <span>Manuals & Instructions</span>
                            <label className="btn btn-sm btn-outline-primary mb-0">
                                <i className="bi bi-upload me-1"></i> Upload
                                <input type="file" hidden onChange={handleUpload} disabled={isUploading} />
                            </label>
                        </div>
                        <div className="card-body">
                            {documents.length === 0 ? (
                                <div className="text-muted small fst-italic">No documents attached.</div>
                            ) : (
                                <ul className="list-group list-group-flush">
                                    {documents.map(doc => (
                                        <li key={doc.document_id} className="list-group-item d-flex justify-content-between align-items-center px-0">
                                            <div>
                                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none fw-bold">
                                                    <i className="bi bi-file-earmark-text me-2"></i>
                                                    {doc.display_name || doc.original_filename}
                                                </a>
                                                <div className="text-muted small" style={{fontSize: '0.7rem'}}>
                                                    Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <button className="btn btn-sm btn-outline-danger border-0" onClick={() => handleDeleteDoc(doc.document_id)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </div>

                {/* CARD 3: TRIGGER LOGIC */}
                <div className="card mb-4 shadow-sm border-primary">
                    <div className="card-header bg-primary text-white fw-bold">Trigger Logic</div>
                    <div className="card-body">
                        <div className="mb-3">
                            <label className="form-label fw-bold">When should this task be created?</label>
                            <div className="btn-group w-100" role="group">
                                <input type="radio" className="btn-check" name="triggerType" id="t1" autoComplete="off" checked={triggerType === 'Calendar'} onChange={() => setTriggerType('Calendar')} />
                                <label className="btn btn-outline-primary" htmlFor="t1"><i className="bi bi-calendar-event me-2"></i>Calendar Schedule</label>

                                <input type="radio" className="btn-check" name="triggerType" id="t2" autoComplete="off" checked={triggerType === 'Usage'} onChange={() => setTriggerType('Usage')} />
                                <label className="btn btn-outline-primary" htmlFor="t2"><i className="bi bi-speedometer2 me-2"></i>Usage / Runtime</label>

                                <input type="radio" className="btn-check" name="triggerType" id="t3" autoComplete="off" checked={triggerType === 'Manual'} onChange={() => setTriggerType('Manual')} />
                                <label className="btn btn-outline-primary" htmlFor="t3"><i className="bi bi-hand-index-thumb me-2"></i>Manual Only</label>
                            </div>
                        </div>

                        {/* CONDITIONAL INPUTS BASED ON TRIGGER */}
                        {triggerType === 'Calendar' && (
                            <div className="alert alert-light border">
                                <div className="mb-3">
                                    <label className="form-label">Recurrence Frequency (Days)</label>
                                    <div className="input-group">
                                        <input type="number" className="form-control" value={recurrenceDays} onChange={e => setRecurrenceDays(e.target.value)} />
                                        <span className="input-group-text">Days</span>
                                    </div>
                                    <div className="form-text">General interval (e.g. 7 for Weekly).</div>
                                </div>
                                
                                <div className="mb-0 border-top pt-2">
                                    <label className="form-label text-primary fw-bold"><i className="bi bi-calendar-week me-1"></i>Force Specific Day (Optional)</label>
                                    <select className="form-select" value={recurrenceDayOfWeek} onChange={e => setRecurrenceDayOfWeek(e.target.value)}>
                                        <option value="">-- None (Use Interval Only) --</option>
                                        <option value="Monday">Monday</option>
                                        <option value="Tuesday">Tuesday</option>
                                        <option value="Wednesday">Wednesday</option>
                                        <option value="Thursday">Thursday</option>
                                        <option value="Friday">Friday</option>
                                        <option value="Saturday">Saturday</option>
                                        <option value="Sunday">Sunday</option>
                                    </select>
                                    <div className="form-text">
                                        If selected, the task only generates on this day of the week. When combined with the recurrence frequency above, the task waits the full interval since the last generation, then fires on the next matching day.
                                    </div>
                                </div>

                                <div className="mb-0 border-top pt-2 mt-2">
                                    <label className="form-label text-primary fw-bold"><i className="bi bi-clock me-1"></i>Scheduled Time (Optional)</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={scheduledTime}
                                        onChange={e => setScheduledTime(e.target.value)}
                                    />
                                    <div className="form-text">
                                        If set, the task will only be created after this time of day (Central Time).
                                        Leave blank to create at the first scheduler run of the day.
                                    </div>
                                </div>
                            </div>
                        )}

                        {triggerType === 'Usage' && (
                            <div className="alert alert-light border">
                                <label className="form-label">Runtime Threshold (Hours)</label>
                                <div className="input-group mb-3">
                                    <input type="number" className="form-control" value={usageThreshold} onChange={e => setUsageThreshold(e.target.value)} />
                                    <span className="input-group-text">Hours of {workstation} time</span>
                                </div>
                                
                                {defId && (
                                    <div className="p-3 bg-white rounded border">
                                        <label className="small text-muted fw-bold">Current Accumulator Status</label>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span>{currentUsage.toFixed(2)} / {usageThreshold} Hours</span>
                                            <span className="badge bg-info text-dark">{getUsagePercent().toFixed(0)}%</span>
                                        </div>
                                        <div className="progress" style={{height: "10px"}}>
                                            <div className="progress-bar progress-bar-striped" role="progressbar" style={{width: `${getUsagePercent()}%`}}></div>
                                        </div>
                                        <div className="form-text small mt-2">
                                            Usage accumulates automatically when production tasks are completed at {workstation}.
                                            It resets to 0 when this maintenance task is completed.
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {triggerType === 'Manual' && (
                            <div className="alert alert-light border">
                                <label className="form-label fw-bold text-primary">
                                    <i className="bi bi-calendar-check me-2"></i>Scheduled Run Date (Optional)
                                </label>
                                <div className="input-group mb-3">
                                    <input 
                                        type="date" 
                                        className="form-control" 
                                        value={nextRunDate} 
                                        onChange={e => setNextRunDate(e.target.value)} 
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label text-primary fw-bold">
                                        <i className="bi bi-clock me-1"></i>Scheduled Time (Optional)
                                    </label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={scheduledTime}
                                        onChange={e => setScheduledTime(e.target.value)}
                                    />
                                    <div className="form-text">
                                        If set, the task will only be created after this time (Central Time) on the scheduled date.
                                        Leave blank to create at the first check run of the day.
                                    </div>
                                </div>
                                <div className="form-text mb-3">
                                    If a date is set, the system will automatically create this task on that date
                                    (at the scheduled time if set, or at the first check run of the day).
                                    Once created, the date is cleared. Leave blank for "On Demand" only.
                                </div>

                                <hr/>
                                
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className="small text-muted">Need it immediately?</span>
                                    <button className="btn btn-sm btn-primary" onClick={handleSpawnNow} disabled={!defId}>
                                        <i className="bi bi-lightning-fill me-1"></i> Spawn Task Now
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Skip if P1 option */}
                        <div className="form-check mt-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="skipIfP1"
                                checked={skipIfPriority1}
                                onChange={e => setSkipIfPriority1(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="skipIfP1">
                                <i className="bi bi-exclamation-triangle-fill text-warning me-1"></i>
                                Skip if a top-priority (P1 or P2) job is scheduled on the same workstation today
                            </label>
                            <div className="form-text">
                                When checked, this task won't be auto-created if a P1 production job
                                is scheduled on the same workstation for that day.
                            </div>
                        </div>
                    </div>
                </div>

            {/* COLUMN 2: PLANNING & STATS */}
            <div className="col-lg-4">
                <div className="card mb-4 shadow-sm">
                    <div className="card-header bg-light fw-bold">Planning Defaults</div>
                    <div className="card-body">
                        <div className="mb-3">
                            <label className="form-label">Estimated Time (Minutes)</label>
                            <input type="number" className="form-control" value={initialEstMinutes} onChange={e => setInitialEstMinutes(e.target.value)} />
                            <div className="form-text">Used for shop capacity planning.</div>
                        </div>
                    </div>
                </div>

                {defId && (
                    <div className="card mb-3 shadow-sm">
                        <div className="card-header bg-light fw-bold">System Status</div>
                        <div className="card-body">
                            <ul className="list-unstyled mb-0">
                                <li className="mb-2">
                                    <strong className="d-block text-muted small">Status</strong>
                                    {isActive ? <span className="badge bg-success">Active - Monitoring</span> : <span className="badge bg-secondary">Paused</span>}
                                </li>
                                <li className="mb-2">
                                    <strong className="d-block text-muted small">Last Generated</strong>
                                    {lastGenerated ? new Date(lastGenerated).toLocaleString() : "Never"}
                                </li>
                                {triggerType === 'Usage' && (
                                    <li>
                                        <strong className="d-block text-muted small">Current Accumulator</strong>
                                        {currentUsage.toFixed(4)} Hours
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
}